import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface MarketGrouping {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface Market {
  id: number;
  name: string;
  country_id: number;
  country_name: string;
}

interface MarketGroupingDialogProps {
  open: boolean;
  grouping: MarketGrouping | null;
  onClose: (success?: boolean) => void;
}

export const MarketGroupingDialog: React.FC<MarketGroupingDialogProps> = ({
  open,
  grouping,
  onClose
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [fetchingGrouping, setFetchingGrouping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [markets, setMarkets] = useState<Market[]>([]);

  // Market search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Market[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Fetch full grouping data when editing
  useEffect(() => {
    const loadGrouping = async () => {
      if (grouping?.id && open) {
        setFetchingGrouping(true);
        setError(null);
        try {
          const response = await authenticatedFetch(
            `/api/marketcompass/admin/market-groupings/${grouping.id}/details`
          );

          if (!response) {
            throw new Error('No response from server');
          }

          if (!response.ok) {
            throw new Error('Failed to load grouping');
          }

          const result = await response.json();
          const fullGrouping = result.grouping;

          setName(fullGrouping.name);
          setDescription(fullGrouping.description || '');
          setIsActive(fullGrouping.is_active);
          setMarkets(fullGrouping.markets || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load grouping');
        } finally {
          setFetchingGrouping(false);
        }
      } else if (!grouping && open) {
        // Reset form for new grouping
        setName('');
        setDescription('');
        setIsActive(true);
        setMarkets([]);
      }
    };

    loadGrouping();
  }, [grouping?.id, open, authenticatedFetch]);

  // Search markets
  useEffect(() => {
    const searchMarkets = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await authenticatedFetch(
          `/api/marketcompass/admin/markets/search?q=${encodeURIComponent(searchTerm)}`
        );

        if (response && response.ok) {
          const result = await response.json();
          // Filter out markets already in the list
          const existingIds = new Set(markets.map(m => m.id));
          setSearchResults((result.markets || []).filter((m: Market) => !existingIds.has(m.id)));
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Error searching markets:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(searchMarkets, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, markets, authenticatedFetch]);

  const handleAddMarket = (market: Market | null) => {
    if (market && !markets.find(m => m.id === market.id)) {
      setMarkets([...markets, market]);
    }
    // Always clear search after selection or typing
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveMarket = async (marketId: number) => {
    if (grouping?.id) {
      // If editing, call API to remove
      try {
        const response = await authenticatedFetch(
          `/api/marketcompass/admin/market-groupings/${grouping.id}/markets/${marketId}`,
          { method: 'DELETE' }
        );

        if (!response || !response.ok) {
          const errorData = response ? await response.json() : {};
          throw new Error(errorData.error || 'Failed to remove market');
        }

        setMarkets(markets.filter(m => m.id !== marketId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove market');
      }
    } else {
      // If creating, just remove from list
      setMarkets(markets.filter(m => m.id !== marketId));
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (markets.length < 2) {
      setError('At least 2 markets are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        is_active: isActive
      };

      let url: string;
      let method: string;

      if (grouping?.id) {
        // Update existing grouping
        url = `/api/marketcompass/admin/market-groupings/${grouping.id}`;
        method = 'PUT';
      } else {
        // Create new grouping
        url = '/api/marketcompass/admin/market-groupings';
        method = 'POST';
      }

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save grouping');
      }

      const result = await response.json();
      const savedGrouping = result.grouping;

      // If creating new grouping, add markets to it
      if (!grouping?.id && markets.length > 0) {
        const marketIds = markets.map(m => m.id);
        const addMarketsResponse = await authenticatedFetch(
          `/api/marketcompass/admin/market-groupings/${savedGrouping.id}/markets`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ market_ids: marketIds })
          }
        );

        if (!addMarketsResponse || !addMarketsResponse.ok) {
          throw new Error('Grouping created but failed to add markets');
        }
      } else if (grouping?.id) {
        // If editing, add any new markets
        const existingIds = new Set(markets.map(m => m.id));
        const newMarkets = markets.filter(m => !existingIds.has(m.id));

        if (newMarkets.length > 0) {
          const marketIds = newMarkets.map(m => m.id);
          await authenticatedFetch(
            `/api/marketcompass/admin/market-groupings/${grouping.id}/markets`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ market_ids: marketIds })
            }
          );
        }
      }

      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && !fetchingGrouping && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        {grouping ? 'Edit Market Grouping' : 'Create Market Grouping'}
      </DialogTitle>
      <DialogContent>
        {fetchingGrouping && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !fetchingGrouping && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            {error}
          </Box>
        )}

        {!fetchingGrouping && (
          <>
            {/* Basic Info */}
            <Box sx={{ mb: 3 }}>
              <TextField
                autoFocus
                label="Grouping Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
            </Box>

            {/* Markets Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Markets ({markets.length})
              </Typography>

              {/* Market Search */}
              <Autocomplete
                key={markets.length} // Force re-render when markets change to clear selection
                freeSolo
                options={searchResults}
                loading={searchLoading}
                inputValue={searchTerm}
                onInputChange={(_, value) => setSearchTerm(value || '')}
                onChange={(_, value) => {
                  if (value && typeof value === 'object') {
                    handleAddMarket(value);
                  }
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.name} (${option.country_name})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and add markets"
                    placeholder="Type to search..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 2 }}
              />

              {/* Selected Markets List */}
              {markets.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                  No markets selected. Search and add at least 2 markets.
                </Typography>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {markets.map((market) => (
                    <ListItem
                      key={market.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveMarket(market.id)}
                          disabled={markets.length <= 2 && !!grouping?.id}
                          title={markets.length <= 2 && !!grouping?.id ? "Cannot remove - minimum 2 markets required" : "Remove market"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={market.name}
                        secondary={market.country_name}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading || fetchingGrouping}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || fetchingGrouping}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
